import { useUTLSettingsContext } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { useMemo } from "react";

export const ROLLSHOP_IMPORT_MODE_MESSAGE =
	"RollShop owns this data (Import mode). Editing here is disabled — make the change in RollShop.";

/**
 * RollShop-owned write gate — a separate axis from the read switch
 * (`Mill.DBRevampEnabled`). One setting per RollShop ENTITY drives the whole
 * duality: `Rollshop.Import<Entity>` (e.g. `Rollshop.ImportPurchaseOrders`).
 *   - '1'  → Automatic (Import mode): the entity's import SP runs and the Mill
 *     write screens for it go read-only.
 *   - else → Manual mode (default '0'): the Mill app writes it; the import is a
 *     no-op. This is today's legacy behavior (imports dead).
 *
 * Returns whether writes are enabled (true = Manual/enabled). The write SPs
 * enforce the same check (defense-in-depth).
 *
 * `entity` selects the per-entity setting. Screens not yet migrated to the
 * per-entity model call it with no argument and fall back to the transitional
 * global `Rollshop.ImportToMillModeManual` ('1' = Manual).
 */
export const useRollShopWriteEnabled = (entity?: string): boolean => {
	const {
		state: { settings },
	} = useUTLSettingsContext();
	return useMemo(() => {
		if (entity) {
			const raw = get(settings, `Rollshop.Import${entity}`, "0");
			return String(raw) !== "1"; // writes enabled unless Automatic/Import
		}
		// Transitional global (screens not yet on the per-entity model).
		const raw = get(settings, "Rollshop.ImportToMillModeManual", "1");
		return String(raw) === "1";
	}, [settings, entity]);
};
